import { CartesianGrid, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts'

import { SegmentedTabs } from '@/components/ds'
import { ChartConfig, ChartContainer } from '@/components/ui/chart'
import { AdminAuthActivity } from '@/interfaces/AdminDashboard'

const chartConfig = {
  logins: { label: 'Přihlášení', color: 'var(--chart-1)' },
  signups: { label: 'Registrace', color: 'var(--chart-2)' },
} satisfies ChartConfig

const PERIOD_OPTIONS = [7, 30, 90] as const

type PeriodDays = (typeof PERIOD_OPTIONS)[number]

interface AuthActivityChartProps {
  data: AdminAuthActivity
  days: PeriodDays
  onDaysChange: (days: PeriodDays) => void
}

const formatNumber = (value: number) => value.toLocaleString('cs-CZ')

/** Čárový graf přihlášení a registrací s tmavým tooltipem (Mercury). */
export const AuthActivityChart = ({ data, days, onDaysChange }: AuthActivityChartProps) => {
  const chartData = data.data.map((day) => ({
    name: new Date(day.date).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' }),
    logins: day.logins,
    signups: day.signups,
  }))

  return (
    <section className="overflow-hidden rounded-lg border">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-3">
        <div className="flex items-center gap-4">
          <h2 className="text-sm font-medium">Přihlášení a registrace</h2>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-chart-1" aria-hidden />
              Přihlášení
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-chart-2" aria-hidden />
              Registrace
            </span>
          </div>
        </div>
        <SegmentedTabs<string>
          value={String(days)}
          onChange={(value) => onDaysChange(Number(value) as PeriodDays)}
          options={PERIOD_OPTIONS.map((option) => ({ value: String(option), label: `${option} dní` }))}
        />
      </div>
      <div className="px-3 py-4">
        <ChartContainer config={chartConfig} className="aspect-auto h-72 w-full">
          <LineChart data={chartData} margin={{ left: 0, right: 12, top: 8 }}>
            <CartesianGrid vertical={false} stroke="var(--border)" />
            <XAxis
              dataKey="name"
              tick={{ fill: 'var(--muted-foreground)' }}
              tickLine={false}
              axisLine={false}
              fontSize={12}
              interval="preserveStartEnd"
              minTickGap={24}
            />
            <YAxis
              tickFormatter={(value) => formatNumber(Number(value))}
              tick={{ fill: 'var(--muted-foreground)' }}
              tickLine={false}
              axisLine={false}
              width={40}
              fontSize={12}
              allowDecimals={false}
            />
            <Tooltip
              cursor={{ stroke: 'var(--border)' }}
              content={({ payload }) => {
                if (!payload?.length) return null
                return (
                  <div className="min-w-36 rounded-md bg-foreground px-3 py-2 text-xs text-background shadow-sm">
                    <p className="mb-1 opacity-70">{payload[0]?.payload.name}</p>
                    {payload.map((item, index) => (
                      <div key={index} className="flex items-center gap-4">
                        <span>{item.name}</span>
                        <span className="ml-auto font-medium tabular">{formatNumber(Number(item.value ?? 0))}</span>
                      </div>
                    ))}
                  </div>
                )
              }}
            />
            <Line type="monotone" dataKey="logins" name="Přihlášení" stroke="var(--color-logins)" dot={false} strokeWidth={1.5} />
            <Line type="monotone" dataKey="signups" name="Registrace" stroke="var(--color-signups)" dot={false} strokeWidth={1.5} />
          </LineChart>
        </ChartContainer>
      </div>
    </section>
  )
}
